











































































































import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import FaqAccordion from "@/components/partials/FaqAccordion.vue";
import EsimInstallMethodsCard from "@/components/partials/CustomerProfile/EsimInstallMethodsCard.vue";
import LoadErrorCard from "@/components/partials/CustomerProfile/LoadErrorCard.vue";
import {GetESIMDataResponse} from "@/interfaces/httpResponses/GetESIMDataResponse";
import {ResellerInformation} from "@/interfaces/entities/CustomerProfile";
import {resolveDeviceContext, SUPPORTED_LANGS} from "@/util/deviceContext";
import {getManualLocale, setManualLocale} from "@/util/localeOverride";
import {DemoScenario} from "@/demo/whitelabelDemoData";
import {isValidShareToken} from "@/util/shareToken";

const DEFAULT_ACCENT_COLOR = "#0B7A73";
const RETRY_MIN_SPIN_DURATION_MS = 800; // matches the `spin 0.8s` CSS animation
const LANGUAGE_LABELS: Record<string, string> = {en: "English", de: "Deutsch", ru: "Русский", es: "Español"};

@Component({
    components: {
        TDSSpinner,
        FaqAccordion,
        EsimInstallMethodsCard,
        LoadErrorCard
    }
})
export default class ESIMInstallation extends Vue {
    // Set by the white-label demo page (WhitelabelDemo.vue), which renders this exact component
    // against fixture data with the branding the viewer is editing. Empty for the real route.
    @Prop({type: Boolean, default: false}) demo!: boolean;
    @Prop({default: "healthy"}) demoScenario!: DemoScenario;
    @Prop({default: null}) brandingOverride!: ResellerInformation | null;

    private loadError: boolean = false;
    private retrying: boolean = false;
    private profileDetails: any = {
        activationCode: "",
        encodedActivationCode: "",
        smdpPlusAddress: "",
        iccid: "",
        volume: 0,
        volumeUnit: "",
        validity: 0,
        validityUnit: "",
        customerProfileUrl: null,
        appleUniversalLink: "",
        androidUniversalLink: "",
        countrySet: ""
    };
    private resellerInformation: ResellerInformation | null = null;
    private showInfo: boolean = false;
    private langMenuOpen: boolean = false;

    // -- Branding --

    // An explicit override wins over whatever the API returned, so the demo page can restyle
    // the live component without going anywhere near the fetch path.
    get branding(): ResellerInformation | null {
        return this.brandingOverride ?? this.resellerInformation;
    }

    get accentColor(): string {
        return this.branding?.accentColor || DEFAULT_ACCENT_COLOR;
    }

    get resellerName(): string {
        return this.branding?.name ?? "";
    }

    get resellerLogoUrl(): string {
        return this.branding?.logoUrl || "";
    }

    get faqUrl(): string {
        return this.branding?.faqUrl || "";
    }

    // -- Subtitle --

    get regionLabel(): string {
        return this.$options.filters?.countryName
            ? (this.$options.filters.countryName(this.profileDetails.countrySet) as string)
            : this.profileDetails.countrySet;
    }

    get volumeText(): string {
        return `${this.profileDetails.volume} ${this.profileDetails.volumeUnit}`;
    }

    get validityText(): string {
        return `${this.profileDetails.validity} ${this.profileDetails.validityUnit}`;
    }

    // -- Locale & FAQ --

    get currentLocale(): string {
        return this.$i18n.locale;
    }

    get languageOptions(): Array<{code: string; label: string}> {
        return SUPPORTED_LANGS.map((code: string) => ({code, label: LANGUAGE_LABELS[code] ?? code.toUpperCase()}));
    }

    get faqItems(): Array<{question: string; answer: string}> {
        return this.$t("esimInstallation.help.questions") as unknown as Array<{question: string; answer: string}>;
    }

    created() {
        if (!getManualLocale()) {
            this.$i18n.locale = resolveDeviceContext().lang;
        }
        this.fetchESIMData();
    }

    mounted() {
        document.addEventListener("click", this.handleOutsideLangClick);
    }

    beforeDestroy() {
        document.removeEventListener("click", this.handleOutsideLangClick);
    }

    // -- User actions --

    private toggleLangMenu() {
        this.langMenuOpen = !this.langMenuOpen;
    }

    private selectLocale(code: string) {
        this.$i18n.locale = code;
        setManualLocale(code);
        this.langMenuOpen = false;
    }

    private handleOutsideLangClick(event: MouseEvent) {
        const wrapper = this.$refs.langDropdown as HTMLElement | undefined;
        if (this.langMenuOpen && wrapper && !wrapper.contains(event.target as Node)) {
            this.langMenuOpen = false;
        }
    }

    private async retryFetch() {
        if (this.retrying) return;
        this.retrying = true;
        // Guarantee a full spin so the click visibly registers even on instant (mock) responses.
        await this.delay(RETRY_MIN_SPIN_DURATION_MS);
        await this.fetchESIMData();
        this.retrying = false;
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve: () => void) => setTimeout(resolve, ms));
    }

    private goBackToBalance() {
        if (this.demo) {
            // No real profile URL to leave for - let the demo page swap its preview instead.
            this.$emit("navigate-back");
            return;
        }
        // This page is typically opened as a standalone share link (SMS/email/QR), so it
        // usually has no in-app history to go back to - prefer the profile URL the API gave us.
        if (this.profileDetails.customerProfileUrl) {
            window.location.href = this.profileDetails.customerProfileUrl;
        } else {
            this.$router.back();
        }
    }

    // -- Data fetching & auth --

    @Watch("demoScenario")
    private onDemoScenarioChanged() {
        if (this.demo) this.fetchESIMData();
    }

    private async fetchESIMData() {
        if (this.demo) {
            // Loaded on demand, so the demo fixtures stay out of this page's own module graph.
            const {demoEsimInstallation} = await import(/* webpackChunkName: "whitelabel-demo-data" */ "@/demo/whitelabelDemoData");
            this.applyEsimPayload(demoEsimInstallation(this.demoScenario));
            return;
        }

        const token = this.$route.query.token ?? "";

        if (!isValidShareToken(token)) {
            this.$toastr.error(this.$t("esimInstallation.error.tokenInvalid")).then(() => location.reload());
        } else {
            try {
                // Always ask for the reseller branding. This page is reached both from the customer
                // profile and straight from the installation URL the API hands out, and that URL
                // (ShareTokenService.getSharePageUrl) carries only a token - so gating branding on a
                // `custom=true` query param left every API-issued link unbranded. Callers may still
                // pass the param; it is simply ignored now.
                const response: GetESIMDataResponse = await this.$store.dispatch("FETCH_ESIM_DATA", {token, includeResellerInfo: true});
                this.applyEsimPayload(response.data);
            } catch (e) {
                this.loadError = true;
            }
        }
    }

    private applyEsimPayload(data: GetESIMDataResponse["data"]) {
        const eSIMProfile = data.esimProfile;
        this.resellerInformation = data.resellerInformation ?? null;
        const balance = data.balances[0];
        const encodedActivationCode = eSIMProfile.activationCode;
        const {smdpPlusAddress, activationCode} = this.getEsimComponents(encodedActivationCode);
        this.$set(this.profileDetails, "activationCode", activationCode);
        this.$set(this.profileDetails, "encodedActivationCode", encodedActivationCode);
        this.$set(this.profileDetails, "smdpPlusAddress", smdpPlusAddress);
        this.$set(this.profileDetails, "iccid", eSIMProfile.iccid);
        this.$set(this.profileDetails, "volume", balance.size.sizeValue);
        this.$set(this.profileDetails, "volumeUnit", balance.size.sizeUnit);
        this.$set(this.profileDetails, "validity", balance.validitySize);
        this.$set(this.profileDetails, "validityUnit", balance.validityUnit);
        this.$set(this.profileDetails, "countrySet", balance.countrySet);
        this.$set(this.profileDetails, "appleUniversalLink", eSIMProfile.appleUniversalLink);
        this.$set(this.profileDetails, "androidUniversalLink", eSIMProfile.androidUniversalLink);
        this.$set(this.profileDetails, "customerProfileUrl", data.customerProfileUrl);

        this.showInfo = true;
        this.loadError = false;
    }

    private getEsimComponents(encodedActivationCode: string): {
        smdpPlusAddress: string | null;
        activationCode: string | null;
    } {
        if (!encodedActivationCode || !encodedActivationCode.includes("$")) {
            return {smdpPlusAddress: null, activationCode: null};
        }

        const parts = encodedActivationCode.split("$");
        return {
            smdpPlusAddress: parts[1] || null,
            activationCode: parts[2] || null
        };
    }
}
